import React from 'react';
import styled from 'styled-components';
import { Tooltip } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import { theme } from '../../styles/theme';
import { useIsMobile } from '../../hooks/useIsMobile';

/** The import screens' building blocks: one label size, one gap, one box height everywhere. */

const LABEL: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 4,
  fontSize: 12,
  lineHeight: '16px',
  color: theme.colors.text.secondary,
};
const HELP: React.CSSProperties = { fontSize: 12, lineHeight: '18px', color: theme.colors.text.secondary };

export const FieldLabel: React.FC<{ children: React.ReactNode; hint?: React.ReactNode }> = ({ children, hint }) => (
  <div style={LABEL}>
    {children}
    {hint && (
      <Tooltip title={hint}>
        <InfoCircleOutlined style={{ cursor: 'help', fontSize: 12 }} />
      </Tooltip>
    )}
  </div>
);

/** Label above the control, help below, the same spacing in every import screen. */
export const Field: React.FC<{
  label: React.ReactNode;
  hint?: React.ReactNode;
  help?: React.ReactNode;
  error?: React.ReactNode;
  children: React.ReactNode;
}> = ({ label, hint, help, error, children }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 0 }}>
    <FieldLabel hint={hint}>{label}</FieldLabel>
    {children}
    {error ? (
      <div style={{ ...HELP, color: theme.colors.trading.sell }}>{error}</div>
    ) : help ? (
      <div style={HELP}>{help}</div>
    ) : null}
  </div>
);

/** Equal columns with one gap; a single column on a phone. */
export const FieldGrid: React.FC<{ columns: number; children: React.ReactNode }> = ({ columns, children }) => {
  const isMobile = useIsMobile();
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: isMobile ? '1fr' : `repeat(${columns}, minmax(0, 1fr))`,
        gap: 16,
        alignItems: 'start',
      }}
    >
      {children}
    </div>
  );
};

/** A figure the user cannot edit, in a box the same height as the inputs beside it. */
export const ReadonlyValue: React.FC<{ children: React.ReactNode; strong?: boolean }> = ({ children, strong }) => (
  <div
    style={{
      height: 32,
      display: 'flex',
      alignItems: 'center',
      padding: '0 11px',
      borderRadius: 6,
      // a quiet fill, not an input's: it reads as a value, never as a field to type into
      border: `1px solid ${theme.colors.border.primary}`,
      background: 'rgba(255, 255, 255, 0.04)',
      color: theme.colors.text.primary,
      fontSize: 14,
      fontWeight: strong ? 600 : 400,
      fontVariantNumeric: 'tabular-nums',
    }}
  >
    {children}
  </div>
);

/** A section's title inside a modal: small, quiet, and the same everywhere. */
export const SectionTitle: React.FC<{ children: React.ReactNode; hint?: React.ReactNode }> = ({ children, hint }) => (
  <div style={{ ...LABEL, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 8 }}>
    {children}
    {hint && (
      <Tooltip title={hint}>
        <InfoCircleOutlined style={{ cursor: 'help', fontSize: 12 }} />
      </Tooltip>
    )}
  </div>
);

/** Full-width number input styling, digits lined up. */
export const NUMBER_INPUT_STYLE: React.CSSProperties = { width: '100%', fontVariantNumeric: 'tabular-nums' };

/** Wrap a table whose number inputs sit in right-aligned columns: their digits align right too. */
export const RightAlignedInputs = styled.div`
  .ant-input-number-input {
    text-align: right;
    font-variant-numeric: tabular-nums;
  }
`;
